import { CsvEnum, Id, IsoDateTime, ListQuery, Paginated, QueryBool, QueryVersion, defineAction, defineEvent, z } from '@mkcore/shared';
import { AUTHENTICATED, adminPermissions as P } from '../permissions';

// Namespace admin (docs/01-architectuur/05-api-en-mcp.md): users, roles, grants, tokens, audit, jobs, choice lists.

const Locale = z.enum(['nl', 'en']);
const UserKind = z.enum(['human', 'agent', 'client']).describe('client: a contact in the client portal (ADR-0028), made by portal.access.invite');
/** Kinds admin.user.create makes; client users come from the portal. */
const CreatableKind = z.enum(['human', 'agent']);
const UserStatus = z.enum(['active', 'deactivated']);
const ObjectType = z.enum(['organisation', 'engagement', 'project']);
const GrantLevel = z.enum(['owner', 'writer', 'reader']);
const ExpectedVersion = z.number().int().positive().optional().describe('Version you read; the update fails if it changed');

// ---------- me ----------

export const DATE_FORMATS = ['dd-mm-yyyy', 'yyyy-mm-dd', 'dd/mm/yyyy', 'mm/dd/yyyy'] as const;
export const DURATION_FORMATS = ['clock', 'decimal'] as const;
const WeekStart = z.number().int().min(1).max(7).describe('ISO weekday the week starts on: 1 Monday, 6 Saturday, 7 Sunday');
/**
 * Your own order of the left menu (CO-1383): the groups in order, and every placed item with its group in order.
 * The web app owns the keys; a page or group it no longer knows is skipped, and a page missing here keeps its default place.
 */
export const NavLayout = z
  .object({
    groups: z.array(z.string().min(1).max(60)).max(50).describe('Group keys in menu order'),
    items: z
      .array(z.object({ to: z.string().min(1).max(200).describe('Path of the page'), group: z.string().min(1).max(60) }))
      .max(300)
      .describe('Pages in menu order, each with the group it sits in'),
  })
  .strict()
  .openapi('NavLayout');

export const Me = z
  .object({
    id: Id,
    tenantId: Id,
    kind: z.enum(['human', 'agent', 'client', 'system']),
    name: z.string(),
    email: z.string().nullable(),
    locale: Locale,
    timezone: z.string(),
    dateFormat: z.enum(DATE_FORMATS),
    durationFormat: z.enum(DURATION_FORMATS).describe('clock shows 1:30, decimal shows 1,50'),
    weekStart: WeekStart,
    navLayout: NavLayout.nullable().describe('Your own menu order; null is the default order'),
    roles: z.array(z.string()),
    permissions: z.array(z.string()),
    tenantWide: z.boolean(),
    authMethod: z.enum(['session', 'api_token', 'oauth', 'internal']),
  })
  .openapi('Me');

export const getMe = defineAction({
  name: 'admin.me.get',
  method: 'get',
  path: '/admin/me',
  permission: AUTHENTICATED,
  input: z.object({}),
  output: Me,
  summary: 'The calling user or agent, with roles and permissions',
});

export const updateMe = defineAction({
  name: 'admin.me.update',
  method: 'patch',
  path: '/admin/me',
  permission: AUTHENTICATED,
  input: z
    .object({
      locale: Locale.optional(),
      timezone: z.string().min(1).max(64).optional(),
      dateFormat: z.enum(DATE_FORMATS).optional(),
      durationFormat: z.enum(DURATION_FORMATS).optional(),
      weekStart: WeekStart.optional(),
      navLayout: NavLayout.nullable().optional().describe('Your own menu order; null restores the default'),
    })
    .strict(),
  output: Me,
  summary: 'Change your own language, time zone, date and duration format, first day of the week (CO-0021) or menu order (CO-1383)',
});

// ---------- company profile (CO-1301, CO-0366) ----------

const Text = (max: number) => z.string().trim().max(max);
const NumberFormat = z
  .string()
  .trim()
  .min(3)
  .max(40)
  .regex(/\{seq(:[1-9])?\}/, 'Must contain {seq} or {seq:N}')
  .describe('Placeholders {year} and {seq:N} (zero padded to N digits), for example {year}-{seq:4} gives 2026-0001');

export const CompanyProfile = z
  .object({
    legalName: z.string(),
    tradeName: z.string(),
    street: z.string(),
    postalCode: z.string(),
    city: z.string(),
    country: z.string().describe('ISO 3166-1 alpha-2'),
    email: z.string(),
    phone: z.string(),
    website: z.string(),
    registrationNumber: z.string().describe('Chamber of commerce (KVK) number'),
    vatNumber: z.string(),
    iban: z.string(),
    bic: z.string(),
    currency: z.string(),
    timezone: z.string(),
    fiscalYearStartMonth: z.number().int(),
    invoiceNumberFormat: z.string(),
    creditNoteNumberFormat: z.string().nullable().describe('Null: credit notes use the invoice series'),
    paymentTermDays: z.number().int(),
    invoiceFooter: z.string().describe('Standard text at the bottom of every invoice'),
    invoiceNotes: z.string().describe('Standard note on new invoices'),
    retention: z
      .object({ inboundMail: z.number().int(), agentTrace: z.number().int(), jobData: z.number().int() })
      .describe('Days after which the nightly cleanup erases the content of incoming mail, the traces of agent runs and the data of finished jobs (CO-0030)'),
    updatedAt: IsoDateTime.nullable(),
    version: z.number().int(),
  })
  .openapi('CompanyProfile');

export const getCompanyProfile = defineAction({
  name: 'admin.company.get',
  method: 'get',
  path: '/admin/company',
  permission: AUTHENTICATED,
  input: z.object({}),
  output: CompanyProfile,
  summary: 'The company profile: legal identity, bank details, numbering and defaults for invoices (CO-1301)',
});

const RetentionDays = z.number().int().min(1).max(3650);

export const updateCompanyProfile = defineAction({
  name: 'admin.company.update',
  method: 'put',
  path: '/admin/company',
  permission: P.keys.update,
  input: z
    .object({
      legalName: Text(200).min(1).optional(),
      tradeName: Text(200).optional(),
      street: Text(200).optional(),
      postalCode: Text(20).optional(),
      city: Text(100).optional(),
      country: z.string().trim().length(2).toUpperCase().optional(),
      email: z.union([z.email().max(320), z.literal('')]).optional(),
      phone: Text(40).optional(),
      website: Text(500).optional(),
      registrationNumber: Text(40).optional(),
      vatNumber: Text(40).optional(),
      iban: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^([A-Z]{2}[0-9]{2}[A-Z0-9]{10,30})?$/, 'An IBAN such as NL91ABNA0417164300')
        .optional(),
      bic: Text(11).toUpperCase().optional(),
      currency: z.string().trim().length(3).toUpperCase().optional(),
      timezone: Text(64).min(1).optional(),
      fiscalYearStartMonth: z.number().int().min(1).max(12).optional(),
      invoiceNumberFormat: NumberFormat.optional(),
      creditNoteNumberFormat: NumberFormat.nullable().optional(),
      paymentTermDays: z.number().int().min(0).max(365).optional(),
      invoiceFooter: z.string().max(2000).optional(),
      invoiceNotes: z.string().max(2000).optional(),
      retention: z
        .object({ inboundMail: RetentionDays.optional(), agentTrace: RetentionDays.optional(), jobData: RetentionDays.optional() })
        .strict()
        .optional(),
      expectedVersion: ExpectedVersion,
    })
    .strict(),
  output: CompanyProfile,
  successStatus: 200,
  summary: 'Change the company profile; only a caller with tenant-wide scope',
  description: 'Approved invoices keep the supplier details they were approved with; a change here affects new invoices only.',
});

// ---------- users (CO-0045, CO-0001, CO-0016) ----------

export const User = z
  .object({
    id: Id,
    kind: UserKind,
    name: z.string(),
    email: z.string().nullable(),
    entraTenantId: z.string().nullable(),
    entraObjectId: z.string().nullable(),
    status: UserStatus,
    locale: Locale,
    timezone: z.string(),
    roleKeys: z.array(z.string()),
    linked: z.boolean().describe('Has signed in at least once'),
    lastLoginAt: IsoDateTime.nullable(),
    deactivatedAt: IsoDateTime.nullable(),
    deletedAt: IsoDateTime.nullable(),
    createdAt: IsoDateTime,
    version: z.number().int(),
    url: z.string().describe('Deep link to the user in the web app'),
  })
  .openapi('User');

export const listUsers = defineAction({
  name: 'admin.user.list',
  method: 'get',
  path: '/admin/users',
  permission: P.keys.read,
  input: ListQuery.extend({
    q: z.string().max(200).optional().describe('Search in name and email'),
    kind: CsvEnum(['human', 'agent', 'client']).optional(),
    status: CsvEnum(['active', 'deactivated']).optional(),
    includeDeleted: QueryBool.optional(),
  }),
  output: Paginated(User),
  summary: 'List users and agent identities',
});

export const getUser = defineAction({
  name: 'admin.user.get',
  method: 'get',
  path: '/admin/users/{id}',
  permission: P.keys.read,
  input: z.object({ id: Id }),
  output: User,
  etag: true,
});

const EntraIds = {
  entraTenantId: z.uuid().nullable().optional().describe('Entra tenant id (tid) of the account'),
  entraObjectId: z.uuid().nullable().optional().describe('Entra object id (oid) of the account'),
};

export const createUser = defineAction({
  name: 'admin.user.create',
  method: 'post',
  path: '/admin/users',
  permission: P.keys.create,
  input: z
    .object({
      kind: CreatableKind.default('human'),
      name: z.string().min(1).max(200),
      email: z.email().max(320).nullable().optional(),
      ...EntraIds,
      roleKeys: z.array(z.string().max(64)).max(20).default([]),
      locale: Locale.default('nl'),
    })
    .strict(),
  output: User,
  summary: 'Add a person to the allowlist or create an agent identity',
});

export const updateUser = defineAction({
  name: 'admin.user.update',
  method: 'patch',
  path: '/admin/users/{id}',
  permission: P.keys.update,
  input: z
    .object({
      id: Id,
      name: z.string().min(1).max(200).optional(),
      email: z.email().max(320).nullable().optional(),
      ...EntraIds,
      roleKeys: z.array(z.string().max(64)).max(20).optional(),
      locale: Locale.optional(),
      timezone: z.string().min(1).max(64).optional(),
      expectedVersion: ExpectedVersion,
    })
    .strict(),
  output: User,
  etag: true,
});

export const deactivateUser = defineAction({
  name: 'admin.user.deactivate',
  method: 'post',
  path: '/admin/users/{id}/deactivate',
  permission: P.keys.delete,
  input: z.object({ id: Id }).strict(),
  output: User,
  successStatus: 200,
  summary: 'Deactivate a user: ends sessions and revokes tokens at once; history stays',
});

export const reactivateUser = defineAction({
  name: 'admin.user.reactivate',
  method: 'post',
  path: '/admin/users/{id}/reactivate',
  permission: P.keys.update,
  input: z.object({ id: Id }).strict(),
  output: User,
  successStatus: 200,
});

export const deleteUser = defineAction({
  name: 'admin.user.delete',
  method: 'delete',
  path: '/admin/users/{id}',
  permission: P.keys.delete,
  input: z.object({ id: Id, expectedVersion: QueryVersion }).strict(),
  output: User,
  etag: true,
  kind: 'destructive',
  summary: 'Move a user to the recycle bin (soft delete); restorable',
});

export const restoreUser = defineAction({
  name: 'admin.user.restore',
  method: 'post',
  path: '/admin/users/{id}/restore',
  permission: P.keys.update,
  input: z.object({ id: Id }).strict(),
  output: User,
  successStatus: 200,
});

// ---------- roles and permissions (CO-0068, CO-0008) ----------

export const Permission = z
  .object({ key: z.string(), module: z.string(), action: z.string(), description: z.string() })
  .openapi('Permission');

export const listPermissions = defineAction({
  name: 'admin.permission.list',
  method: 'get',
  path: '/admin/permissions',
  permission: P.keys.read,
  input: z.object({ module: z.string().max(64).optional() }),
  output: z.object({ items: z.array(Permission) }),
  summary: 'The permission catalog: module:action',
});

export const Role = z
  .object({
    id: Id,
    key: z.string(),
    name: z.string(),
    description: z.string(),
    isSystem: z.boolean(),
    tenantWideScope: z.boolean(),
    permissionKeys: z.array(z.string()),
    memberCount: z.number().int(),
    version: z.number().int(),
    url: z.string(),
  })
  .openapi('Role');

export const listRoles = defineAction({
  name: 'admin.role.list',
  method: 'get',
  path: '/admin/roles',
  permission: P.keys.read,
  input: z.object({}),
  output: z.object({ items: z.array(Role) }),
});

export const getRole = defineAction({
  name: 'admin.role.get',
  method: 'get',
  path: '/admin/roles/{id}',
  permission: P.keys.read,
  input: z.object({ id: Id }),
  output: Role,
  etag: true,
});

const RoleKey = z
  .string()
  .min(2)
  .max(64)
  .regex(/^[a-z][a-z0-9-]*$/, 'lowercase letters, digits and dashes');

export const createRole = defineAction({
  name: 'admin.role.create',
  method: 'post',
  path: '/admin/roles',
  permission: P.keys.create,
  input: z
    .object({
      key: RoleKey,
      name: z.string().min(1).max(100),
      description: z.string().max(1000).default(''),
      tenantWideScope: z.boolean().default(false),
      permissionKeys: z.array(z.string().max(100)).max(500).default([]),
    })
    .strict(),
  output: Role,
  summary: 'Compose a role from the permission catalog',
});

export const updateRole = defineAction({
  name: 'admin.role.update',
  method: 'patch',
  path: '/admin/roles/{id}',
  permission: P.keys.update,
  input: z
    .object({
      id: Id,
      name: z.string().min(1).max(100).optional(),
      description: z.string().max(1000).optional(),
      tenantWideScope: z.boolean().optional(),
      permissionKeys: z.array(z.string().max(100)).max(500).optional(),
      expectedVersion: ExpectedVersion,
    })
    .strict(),
  output: Role,
  etag: true,
  summary: 'Change a role; the admin role is fixed and cannot be changed',
});

export const deleteRole = defineAction({
  name: 'admin.role.delete',
  method: 'delete',
  path: '/admin/roles/{id}',
  permission: P.keys.delete,
  input: z.object({ id: Id, expectedVersion: QueryVersion }).strict(),
  output: z.object({ id: Id, deleted: z.literal(true) }),
  etag: true,
  kind: 'destructive',
});

// ---------- grants: object scope (CO-0088, CO-0203) ----------

export const Grant = z
  .object({
    id: Id,
    principalId: Id,
    principalName: z.string(),
    objectType: ObjectType,
    objectId: Id,
    level: GrantLevel,
    createdAt: IsoDateTime,
  })
  .openapi('Grant');

export const listGrants = defineAction({
  name: 'admin.grant.list',
  method: 'get',
  path: '/admin/grants',
  permission: AUTHENTICATED,
  input: ListQuery.extend({
    principalId: Id.optional(),
    objectType: ObjectType.optional(),
    objectId: Id.optional(),
  }),
  output: Paginated(Grant),
  summary: 'Who has access to which organisation, engagement or project',
  description: 'Admins see all grants; others see their own grants and the grants on objects they own.',
});

export const createGrant = defineAction({
  name: 'admin.grant.create',
  method: 'post',
  path: '/admin/grants',
  permission: AUTHENTICATED,
  input: z.object({ principalId: Id, objectType: ObjectType, objectId: Id, level: GrantLevel }).strict(),
  output: Grant,
  summary: 'Give a user or agent access to an organisation, engagement or project',
  description: 'Needs admin:create, or owner level on the object. Replaces the level of an existing grant.',
});

export const deleteGrant = defineAction({
  name: 'admin.grant.delete',
  method: 'delete',
  path: '/admin/grants/{id}',
  permission: AUTHENTICATED,
  input: z.object({ id: Id }).strict(),
  output: z.object({ id: Id, deleted: z.literal(true) }),
  kind: 'destructive',
});

// ---------- API tokens (CO-0001, CO-0113) ----------

const TokenScope = z
  .object({ organisationIds: z.array(Id).max(200).default([]), projectIds: z.array(Id).max(200).default([]) })
  .openapi('TokenScope');
const TokenLimits = z
  .object({ perMinute: z.number().int().min(1).max(10_000).optional(), perDay: z.number().int().min(1).max(1_000_000).optional() })
  .openapi('TokenLimits');

export const ApiToken = z
  .object({
    id: z.string(),
    principalId: Id,
    name: z.string(),
    start: z.string().nullable().describe('First characters, to recognise the token'),
    enabled: z.boolean(),
    expiresAt: IsoDateTime.nullable(),
    lastUsedAt: IsoDateTime.nullable(),
    createdAt: IsoDateTime,
    kind: z.enum(['api', 'editor']).describe('api: an API token (mkc_…); editor: an editor key for a WakaTime plugin (waka_…), only for editor activity (ADR-0023)'),
    toolAllowlist: z.array(z.string()).nullable().describe('Tool names or patterns such as time.entry.* and dev.*:read; null allows all'),
    readOnly: z.boolean().describe('Only tools and requests that read; the emergency brake (CO-0018)'),
    scope: TokenScope.nullable().describe('Only these organisations and projects (CO-0056); null reaches all the principal can'),
    limits: TokenLimits.nullable().describe('Requests per minute and per day on top of the limits of the principal (CO-0128)'),
  })
  .openapi('ApiToken');

export const listTokens = defineAction({
  name: 'admin.token.list',
  method: 'get',
  path: '/admin/tokens',
  permission: AUTHENTICATED,
  input: z.object({ principalId: Id.optional().describe('Default: your own tokens; others need admin:read') }),
  output: z.object({ items: z.array(ApiToken) }),
});

export const createToken = defineAction({
  name: 'admin.token.create',
  method: 'post',
  path: '/admin/tokens',
  permission: AUTHENTICATED,
  input: z
    .object({
      principalId: Id.optional().describe('Default: yourself; a token for another user or agent needs admin:create'),
      kind: z
        .enum(['api', 'editor'])
        .default('api')
        .describe('editor: a key for a WakaTime plugin (waka_ and a UUID v4, the shape wakatime-cli accepts) that may only record and read editor activity; no toolAllowlist, readOnly or scope (ADR-0023)'),
      name: z.string().min(1).max(100),
      expiresInDays: z.number().int().min(1).max(730).nullable().default(365),
      toolAllowlist: z.array(z.string().max(100)).max(500).nullable().default(null),
      readOnly: z.boolean().default(false),
      scope: TokenScope.nullable().default(null),
      limits: TokenLimits.nullable().default(null),
    })
    .strict(),
  output: ApiToken.extend({ token: z.string().describe('Shown once; store it now') }),
  secret: true,
  summary: 'Issue an API token; the token is shown once and stored hashed',
});

export const updateToken = defineAction({
  name: 'admin.token.update',
  method: 'patch',
  path: '/admin/tokens/{id}',
  permission: AUTHENTICATED,
  successStatus: 200,
  input: z
    .object({
      id: z.string().min(1).max(100),
      readOnly: z.boolean().optional(),
      toolAllowlist: z.array(z.string().max(100)).max(500).nullable().optional(),
      scope: TokenScope.nullable().optional(),
      limits: TokenLimits.nullable().optional(),
    })
    .strict(),
  output: ApiToken,
  summary: 'Changes what a token may do: read-only, its tool allowlist, its organisations and projects, its limits',
  description: 'Your own tokens; those of others need admin:update. Takes effect on the next request.',
});

export const revokeToken = defineAction({
  name: 'admin.token.revoke',
  method: 'post',
  path: '/admin/tokens/{id}/revoke',
  permission: AUTHENTICATED,
  input: z.object({ id: z.string().min(1).max(100) }).strict(),
  output: ApiToken,
  successStatus: 200,
  kind: 'destructive',
});

// ---------- audit log (CO-0002) and call log (CO-0116) ----------

export const AuditLogEntry = z
  .object({
    id: Id,
    occurredAt: IsoDateTime,
    actorKind: z.enum(['user', 'agent', 'system']),
    actorId: Id.nullable(),
    actorName: z.string().nullable(),
    onBehalfOf: Id.nullable(),
    agentRunId: Id.nullable(),
    authMethod: z.string(),
    action: z.string(),
    entityType: z.string().nullable(),
    entityId: Id.nullable(),
    changes: z.record(z.string(), z.unknown()).nullable(),
    requestId: z.string().nullable(),
  })
  .openapi('AuditLogEntry');

const Period = {
  from: IsoDateTime.optional().describe('Inclusive start'),
  to: IsoDateTime.optional().describe('Exclusive end'),
};

export const listAuditLog = defineAction({
  name: 'admin.auditLog.list',
  method: 'get',
  path: '/admin/audit-logs',
  permission: P.keys.read,
  input: ListQuery.extend({
    entityType: z.string().max(64).optional(),
    entityId: Id.optional(),
    actorId: Id.optional(),
    agentRunId: Id.optional().describe('The changes one agent run made (CO-0969)'),
    action: z.string().max(100).optional().describe('Exact action or a prefix ending with *, e.g. auth.*'),
    ...Period,
  }),
  output: Paginated(AuditLogEntry),
  summary: 'The audit trail, newest first, filterable by entity, actor and period',
});

export const ApiCall = z
  .object({
    id: Id,
    occurredAt: IsoDateTime,
    channel: z.enum(['rest', 'mcp']),
    principalId: Id.nullable(),
    actorKind: z.string().nullable(),
    authMethod: z.string().nullable(),
    tool: z.string().nullable(),
    method: z.string().nullable(),
    path: z.string().nullable(),
    status: z.number().int(),
    durationMs: z.number().int(),
    errorCode: z.string().nullable(),
    requestId: z.string().nullable(),
  })
  .openapi('ApiCall');

export const listApiCalls = defineAction({
  name: 'admin.apiCallLog.list',
  method: 'get',
  path: '/admin/api-call-logs',
  permission: P.keys.read,
  input: ListQuery.extend({
    principalId: Id.optional(),
    tool: z.string().max(100).optional(),
    channel: z.enum(['rest', 'mcp']).optional(),
    errorsOnly: QueryBool.optional(),
    ...Period,
  }),
  output: Paginated(ApiCall),
  summary: 'Log of REST and MCP calls (90 days), to debug agent behaviour',
});

// ---------- jobs (CO-0701, CO-0702, CO-0961) ----------

export const JOB_STATUSES = ['waiting', 'running', 'paused', 'succeeded', 'failed', 'cancelled', 'unknown'] as const;
export const JOB_TRIGGERS = ['schedule', 'event', 'webhook', 'manual', 'agent'] as const;
const JobStatus = z.enum(JOB_STATUSES);
const WorkflowId = z.string().min(1).max(300);
export const JobSummary = z
  .object({
    workflowId: z.string(),
    jobType: z.string(),
    trigger: z.string(),
    targetType: z.string().nullable(),
    targetId: z.string().nullable(),
    organisationId: Id.nullable().describe('The client the run is about'),
    tags: z.array(z.string()),
    status: JobStatus,
    outcome: z
      .enum(['partial', 'failed', 'skipped'])
      .nullable()
      .describe('Set by the job: partial, the run finished but some items failed (see the output); failed, the work it ran failed (a routine whose agent run failed); skipped, nothing to do'),
    dbosStatus: z.string().nullable(),
    startedBy: Id.nullable(),
    parentWorkflowId: z.string().nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime.nullable(),
    durationMs: z.number().int().nullable().describe('From start to end; null while it runs'),
    error: z.string().nullable(),
    failedAt: IsoDateTime.nullable(),
    resolvedAt: IsoDateTime.nullable().describe('A failed run someone marked as dealt with'),
    pausedAt: IsoDateTime.nullable(),
    stuckAt: IsoDateTime.nullable().describe('No progress for longer than the stuck limit'),
    autoResumedAt: IsoDateTime.nullable().describe('Stuck once, and resumed by the app itself because its job type is restartable (CO-0726)'),
  })
  .openapi('JobSummary');

export const JobStep = z
  .object({
    stepId: z.number().int().describe('DBOS step number of the first attempt'),
    name: z.string(),
    status: z.string(),
    attempts: z.number().int(),
    error: z.string().nullable(),
    output: z.unknown().describe('Masked and cut short'),
    startedAt: IsoDateTime.nullable(),
    completedAt: IsoDateTime.nullable(),
    durationMs: z.number().int().nullable(),
    childWorkflowId: z.string().nullable(),
  })
  .openapi('JobStep');

export const JobLogEntry = z
  .object({
    at: IsoDateTime,
    level: z.enum(['info', 'warn', 'error']),
    msg: z.string(),
    step: z.string(),
    attempt: z.number().int(),
    fields: z.record(z.string(), z.unknown()).nullable(),
  })
  .openapi('JobLogEntry');

export const JobDetail = JobSummary.extend({
  input: z.unknown().describe('Masked'),
  output: z.unknown().describe('Masked'),
  steps: z.array(JobStep),
  children: z.array(JobSummary).describe('Runs this run started'),
  logs: z.array(JobLogEntry).describe('Log lines of the steps, oldest first, masked'),
}).openapi('JobDetail');

export const jobMetrics = defineAction({
  name: 'admin.job.metrics',
  method: 'get',
  path: '/admin/job-metrics',
  permission: P.keys.read,
  input: z.object({ from: IsoDateTime.describe('Inclusive'), to: IsoDateTime.describe('Exclusive') }),
  output: z.object({
    from: IsoDateTime,
    to: IsoDateTime,
    items: z.array(
      z.object({
        jobType: z.string(),
        runs: z.number().int(),
        waiting: z.number().int(),
        running: z.number().int(),
        paused: z.number().int(),
        succeeded: z.number().int(),
        failed: z.number().int().describe('Ended in error, or ended with outcome failed (a routine whose agent run failed); also when resolved or recovered since'),
        cancelled: z.number().int(),
        unknown: z.number().int(),
        resolved: z.number().int().describe('Failed and marked as resolved; part of failed'),
        openFailures: z.number().int().describe('Failed and not recovered or resolved'),
        medianMs: z.number().int().nullable(),
        p90Ms: z.number().int().nullable(),
      }),
    ),
    capped: z.boolean().describe('The period had more than 5000 runs: runs, failed, resolved and openFailures are complete; the other statuses and the durations are of the newest 5000'),
  }),
  summary: 'Per job type in a period: runs, how they ended, what waits, open failures and the median and p90 duration (CO-0975)',
});

export const listJobs = defineAction({
  name: 'admin.job.list',
  method: 'get',
  path: '/admin/jobs',
  permission: P.keys.read,
  input: ListQuery.extend({
    jobType: z.string().max(100).optional(),
    status: CsvEnum(JOB_STATUSES).optional(),
    trigger: CsvEnum(JOB_TRIGGERS).optional(),
    targetType: z.string().max(64).optional(),
    targetId: z.string().max(300).optional(),
    tag: z.string().max(200).optional(),
    organisationId: Id.optional().describe('Only the runs about this client'),
    parentWorkflowId: WorkflowId.optional().describe('Only the runs this run started'),
    openFailures: QueryBool.optional().describe('Only failed runs that are not recovered or resolved'),
    ...Period,
  }),
  output: Paginated(JobSummary),
  summary: 'Job runs with their status and duration, newest first; filter on type, status, trigger, target, tag, client and period',
});

export const getJob = defineAction({
  name: 'admin.job.get',
  method: 'get',
  path: '/admin/jobs/{id}',
  permission: P.keys.read,
  input: z.object({ id: WorkflowId }),
  output: JobDetail,
  summary: 'One job run with its steps and attempts, input, output, errors, child runs and log lines',
});

export const JobType = z
  .object({
    type: z.string(),
    title: z.string(),
    description: z.string(),
    inputSchema: z.record(z.string(), z.unknown()).describe('JSON schema of the input'),
    maxDurationMs: z.number().int().nullable(),
  })
  .openapi('JobType');

export const listJobTypes = defineAction({
  name: 'admin.jobType.list',
  method: 'get',
  path: '/admin/job-types',
  permission: P.keys.read,
  input: z.object({}),
  output: z.object({ items: z.array(JobType) }),
  summary: 'The jobs that can be started by hand, with the schema of their input (CO-0710)',
});

export const startJob = defineAction({
  name: 'admin.job.start',
  method: 'post',
  path: '/admin/jobs',
  permission: P.keys.update,
  input: z
    .object({
      jobType: z.string().min(1).max(100).describe('From admin.jobType.list'),
      input: z.record(z.string(), z.unknown()).optional().describe('The typed input of the job'),
      idempotencyKey: z.string().min(1).max(100).optional().describe('The same key starts the job once; a second call returns the existing run'),
    })
    .strict(),
  output: z.object({
    workflowId: z.string().describe('The run id for admin.job.get'),
    jobType: z.string(),
    status: JobStatus,
    joined: z.boolean().describe('true when a run with this id already existed'),
  }),
  successStatus: 202,
  summary: 'Start a durable job by hand and get its run id to follow it (CO-0111, CO-0710)',
});

export const retryJob = defineAction({
  name: 'admin.job.retry',
  method: 'post',
  path: '/admin/jobs/{id}/retry',
  permission: P.keys.update,
  input: z.object({ id: z.string().min(1).max(300) }).strict(),
  output: z.object({ workflowId: z.string(), forkedFrom: z.string(), fromStep: z.number().int() }),
  successStatus: 200,
  summary: 'Resume a failed job from the failed step; earlier steps are not repeated',
});

export const cancelJob = defineAction({
  name: 'admin.job.cancel',
  method: 'post',
  path: '/admin/jobs/{id}/cancel',
  permission: P.keys.update,
  input: z.object({ id: z.string().min(1).max(300) }).strict(),
  output: z.object({ workflowId: z.string(), status: z.literal('cancelled') }),
  successStatus: 200,
  kind: 'destructive',
});

export const pauseJob = defineAction({
  name: 'admin.job.pause',
  method: 'post',
  path: '/admin/jobs/{id}/pause',
  permission: P.keys.update,
  input: z.object({ id: WorkflowId }).strict(),
  output: JobSummary,
  successStatus: 200,
  summary: 'Pause a waiting or running job and its child runs at the next step; admin.job.resume continues it',
});

export const resumeJob = defineAction({
  name: 'admin.job.resume',
  method: 'post',
  path: '/admin/jobs/{id}/resume',
  permission: P.keys.update,
  input: z.object({ id: WorkflowId }).strict(),
  output: JobSummary,
  successStatus: 200,
  summary: 'Continue a paused, cancelled or stuck job after its last completed step',
});

export const resolveJob = defineAction({
  name: 'admin.job.resolve',
  method: 'post',
  path: '/admin/jobs/{id}/resolve',
  permission: P.keys.update,
  input: z.object({ id: WorkflowId, note: z.string().max(500).optional() }).strict(),
  output: JobSummary,
  successStatus: 200,
  summary: 'Mark a failed run as dealt with, without running it again',
});

export const JOB_BULK_ACTIONS = ['retry', 'cancel', 'pause', 'resume', 'resolve'] as const;
export const bulkJobs = defineAction({
  name: 'admin.job.bulk',
  method: 'post',
  path: '/admin/jobs/bulk',
  permission: P.keys.update,
  input: z
    .object({
      action: z.enum(JOB_BULK_ACTIONS),
      ids: z.array(WorkflowId).min(1).max(100).describe('Run ids, for example from admin.job.list with openFailures=true'),
    })
    .strict(),
  output: z.object({
    results: z.array(z.object({ workflowId: z.string(), ok: z.boolean(), newWorkflowId: z.string().nullable(), error: z.string().nullable() })),
  }),
  successStatus: 200,
  summary: 'Retry, cancel, pause, resume or resolve many runs at once; each one is audited and reported',
});

export const QueuedJob = z
  .object({
    workflowId: z.string(),
    jobType: z.string(),
    queueName: z.string().nullable(),
    status: z.string(),
    createdAt: IsoDateTime,
    partitionKey: z.string().nullable(),
  })
  .openapi('QueuedJob');

export const listJobQueue = defineAction({
  name: 'admin.job.queue',
  method: 'get',
  path: '/admin/job-queue',
  permission: P.keys.read,
  input: z.object({ queueName: z.string().max(100).optional() }),
  output: z.object({
    queues: z.array(
      z.object({ name: z.string(), concurrency: z.number().int().nullable(), partitionConcurrency: z.number().int().nullable(), waiting: z.number().int() }),
    ),
    items: z.array(QueuedJob),
  }),
  summary: 'The queues with their limits, and the runs that wait on them, oldest first',
});

// ---------- schedules (CO-0724, CO-0697) ----------

export const Schedule = z
  .object({
    name: z.string(),
    workflowName: z.string(),
    cron: z.string(),
    timezone: z.string().nullable(),
    status: z.enum(['active', 'paused']),
    automaticBackfill: z.boolean(),
    lastFiredAt: IsoDateTime.nullable(),
    nextFireAt: IsoDateTime.nullable(),
    lastRun: z.object({ workflowId: z.string(), status: JobStatus, createdAt: IsoDateTime }).nullable(),
  })
  .openapi('Schedule');

export const listSchedules = defineAction({
  name: 'admin.schedule.list',
  method: 'get',
  path: '/admin/schedules',
  permission: P.keys.read,
  input: z.object({}),
  output: z.object({ items: z.array(Schedule) }),
  summary: 'The recurring jobs with their schedule, status, last run and next run',
});

const ScheduleName = z.string().min(1).max(200);
export const pauseSchedule = defineAction({
  name: 'admin.schedule.pause',
  method: 'post',
  path: '/admin/schedules/{name}/pause',
  permission: P.keys.update,
  input: z.object({ name: ScheduleName }).strict(),
  output: Schedule,
  successStatus: 200,
  summary: 'Stop a recurring job until it is resumed; the pause survives a deploy',
});

export const resumeSchedule = defineAction({
  name: 'admin.schedule.resume',
  method: 'post',
  path: '/admin/schedules/{name}/resume',
  permission: P.keys.update,
  input: z.object({ name: ScheduleName }).strict(),
  output: Schedule,
  successStatus: 200,
});

export const runSchedule = defineAction({
  name: 'admin.schedule.run',
  method: 'post',
  path: '/admin/schedules/{name}/run',
  permission: P.keys.update,
  input: z.object({ name: ScheduleName }).strict(),
  output: z.object({ workflowId: z.string() }),
  successStatus: 202,
  summary: 'Run a recurring job now, once, outside its schedule',
});

// ---------- choice lists (CO-0039) ----------

export const ChoiceValue = z
  .object({
    id: Id,
    listId: Id,
    value: z.string(),
    label: z.string(),
    position: z.number().int(),
    active: z.boolean(),
    version: z.number().int(),
  })
  .openapi('ChoiceValue');

export const ChoiceList = z
  .object({
    id: Id,
    key: z.string(),
    name: z.string(),
    description: z.string(),
    values: z.array(ChoiceValue),
    version: z.number().int(),
    url: z.string(),
  })
  .openapi('ChoiceList');

export const listChoiceLists = defineAction({
  name: 'admin.choiceList.list',
  method: 'get',
  path: '/admin/choice-lists',
  permission: AUTHENTICATED,
  input: z.object({ key: z.string().max(100).optional() }),
  output: z.object({ items: z.array(ChoiceList) }),
  summary: 'Manageable choice lists (categories, sources) with their values',
});

export const getChoiceList = defineAction({
  name: 'admin.choiceList.get',
  method: 'get',
  path: '/admin/choice-lists/{id}',
  permission: AUTHENTICATED,
  input: z.object({ id: Id }),
  output: ChoiceList,
  etag: true,
});

const ValueKey = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[a-z0-9][a-z0-9_.-]*$/, 'lowercase letters, digits, dot, dash or underscore');

export const createChoiceList = defineAction({
  name: 'admin.choiceList.create',
  method: 'post',
  path: '/admin/choice-lists',
  permission: P.keys.create,
  input: z
    .object({
      key: z
        .string()
        .min(2)
        .max(100)
        .regex(/^[a-z][a-zA-Z0-9.]*$/),
      name: z.string().min(1).max(200),
      description: z.string().max(1000).default(''),
      values: z
        .array(z.object({ value: ValueKey.optional(), label: z.string().min(1).max(200) }))
        .max(500)
        .default([]),
    })
    .strict(),
  output: ChoiceList,
});

export const updateChoiceList = defineAction({
  name: 'admin.choiceList.update',
  method: 'patch',
  path: '/admin/choice-lists/{id}',
  permission: P.keys.update,
  input: z
    .object({
      id: Id,
      name: z.string().min(1).max(200).optional(),
      description: z.string().max(1000).optional(),
      expectedVersion: ExpectedVersion,
    })
    .strict(),
  output: ChoiceList,
  etag: true,
});

export const deleteChoiceList = defineAction({
  name: 'admin.choiceList.delete',
  method: 'delete',
  path: '/admin/choice-lists/{id}',
  permission: P.keys.delete,
  input: z.object({ id: Id, expectedVersion: QueryVersion }).strict(),
  output: z.object({ id: Id, deleted: z.literal(true) }),
  etag: true,
  kind: 'destructive',
});

export const reorderChoiceList = defineAction({
  name: 'admin.choiceList.reorder',
  method: 'post',
  path: '/admin/choice-lists/{id}/reorder',
  permission: P.keys.update,
  input: z.object({ id: Id, valueIds: z.array(Id).min(1).max(500).describe('All value ids in the new order') }).strict(),
  output: ChoiceList,
  successStatus: 200,
});

export const pasteChoiceValues = defineAction({
  name: 'admin.choiceList.paste',
  method: 'post',
  path: '/admin/choice-lists/{id}/paste',
  permission: P.keys.update,
  input: z
    .object({
      id: Id,
      text: z.string().max(50_000).describe('One label per line; existing labels are skipped'),
    })
    .strict(),
  output: ChoiceList.extend({ added: z.number().int(), skipped: z.number().int() }),
  successStatus: 200,
  summary: 'Bulk paste values, one per line',
});

export const createChoiceValue = defineAction({
  name: 'admin.choiceValue.create',
  method: 'post',
  path: '/admin/choice-lists/{listId}/values',
  permission: P.keys.update,
  input: z.object({ listId: Id, value: ValueKey.optional(), label: z.string().min(1).max(200) }).strict(),
  output: ChoiceValue,
});

export const updateChoiceValue = defineAction({
  name: 'admin.choiceValue.update',
  method: 'patch',
  path: '/admin/choice-values/{id}',
  permission: P.keys.update,
  input: z
    .object({
      id: Id,
      label: z.string().min(1).max(200).optional().describe('Rename; the stored value key stays the same'),
      active: z.boolean().optional(),
      expectedVersion: ExpectedVersion,
    })
    .strict(),
  output: ChoiceValue,
  etag: true,
});

// ---------- secrets (CO-0027, CO-0015) ----------

export const SECRET_NAME = /^[a-z][a-zA-Z0-9]*([.][a-z][a-zA-Z0-9]*)+$/;
export const OP_REFERENCE = /^op:\/\/[^/\r\n]+\/[^/\r\n]+(\/[^/\r\n]+)+$/;
const SecretName = z
  .string()
  .max(100)
  .regex(SECRET_NAME, 'Dotted name such as forgejo.webhook or havep.feedbackApiKey')
  .describe('What the secret is for, as namespace.purpose, for example forgejo.webhook');
const SecretValue = z.string().min(8).max(8192).describe('The secret itself; stored encrypted and never returned');
const OpReference = z.string().max(500).regex(OP_REFERENCE, 'A 1Password reference: op://vault/item/field').describe('A 1Password reference op://vault/item/field, resolved at run time');

export const Secret = z
  .object({
    id: Id,
    name: z.string(),
    organisationId: Id.nullable().describe('The client the secret belongs to; null for the whole company'),
    kind: z.enum(['value', 'reference']),
    set: z.literal(true).describe('A secret is never returned; this only says that it exists'),
    reference: z.string().nullable().describe('The 1Password reference of a reference secret; null for an encrypted value'),
    description: z.string(),
    rotatedAt: IsoDateTime,
    rotatedBy: Id.nullable(),
    createdAt: IsoDateTime,
    version: z.number().int(),
  })
  .openapi('Secret');

export const listSecrets = defineAction({
  name: 'admin.secret.list',
  method: 'get',
  path: '/admin/secrets',
  permission: P.keys.read,
  input: z.object({
    organisationId: Id.optional().describe('Only the secrets of this client'),
    name: z.string().max(100).optional(),
  }),
  output: z.object({ items: z.array(Secret) }),
  summary: 'The secrets of the company and its clients: name, kind and last rotation, never the value',
});

export const createSecret = defineAction({
  name: 'admin.secret.create',
  method: 'post',
  path: '/admin/secrets',
  permission: P.keys.update,
  input: z
    .object({
      name: SecretName,
      organisationId: Id.nullable().default(null),
      value: SecretValue.optional(),
      reference: OpReference.optional(),
      description: z.string().max(500).default(''),
    })
    .strict()
    .describe('Give either value or reference, not both'),
  output: Secret,
});

export const rotateSecret = defineAction({
  name: 'admin.secret.rotate',
  method: 'post',
  path: '/admin/secrets/{id}/rotate',
  permission: P.keys.update,
  input: z
    .object({
      id: Id,
      value: SecretValue.optional(),
      reference: OpReference.optional(),
      description: z.string().max(500).optional(),
      expectedVersion: ExpectedVersion,
    })
    .strict()
    .describe('Give either value or reference, not both'),
  output: Secret,
  etag: true,
  successStatus: 200,
});

export const deleteSecret = defineAction({
  name: 'admin.secret.delete',
  method: 'delete',
  path: '/admin/secrets/{id}',
  permission: P.keys.delete,
  input: z.object({ id: Id, expectedVersion: QueryVersion }).strict(),
  output: z.object({ id: Id, deleted: z.literal(true) }),
  etag: true,
});

// ---------- webhooks (CO-0717, CO-0121, CO-0125, CO-0967) ----------

export const WEBHOOK_STATUSES = ['received', 'queued', 'processed', 'ignored', 'failed', 'rejected'] as const;
export const WebhookDelivery = z
  .object({
    id: Id,
    source: z.string(),
    key: z.string().describe('The key in the path: an organisation id or a fixed name such as feedback'),
    organisationId: Id.nullable(),
    deliveryId: z.string().describe('The delivery id of the source; deliveries are deduplicated on it'),
    event: z.string(),
    status: z.enum(WEBHOOK_STATUSES),
    error: z.string().nullable(),
    workflowId: z.string().nullable(),
    attempts: z.number().int(),
    receivedAt: IsoDateTime,
    processedAt: IsoDateTime.nullable(),
  })
  .openapi('WebhookDelivery');

export const WebhookDeliveryDetail = WebhookDelivery.extend({
  headers: z.record(z.string(), z.string()).describe('Received headers; signatures and tokens are masked'),
  payload: z.unknown().nullable(),
  result: z.unknown().nullable(),
}).openapi('WebhookDeliveryDetail');

export const listWebhookDeliveries = defineAction({
  name: 'admin.webhook.list',
  method: 'get',
  path: '/admin/webhooks',
  permission: P.keys.read,
  input: ListQuery.extend({
    source: z.string().max(50).optional(),
    status: CsvEnum(WEBHOOK_STATUSES).optional(),
    event: z.string().max(100).optional(),
    organisationId: Id.optional(),
    ...Period,
  }),
  output: Paginated(WebhookDelivery),
  summary: 'The receipt log of incoming webhooks, newest first, with status and error',
});

export const getWebhookDelivery = defineAction({
  name: 'admin.webhook.get',
  method: 'get',
  path: '/admin/webhooks/{id}',
  permission: P.keys.read,
  input: z.object({ id: Id }),
  output: WebhookDeliveryDetail,
});

export const replayWebhookDelivery = defineAction({
  name: 'admin.webhook.replay',
  method: 'post',
  path: '/admin/webhooks/{id}/replay',
  permission: P.keys.update,
  input: z.object({ id: Id }).strict(),
  output: WebhookDelivery,
  successStatus: 200,
  summary: 'Processes a received delivery again, for example after a failure; handlers tolerate a second run',
});

// ---------- events ----------

export const userChanged = defineEvent({
  type: 'admin.user.changed',
  version: 1,
  payload: z.object({
    userId: Id,
    change: z.enum(['created', 'updated', 'deactivated', 'reactivated', 'deleted', 'restored']),
  }),
});
export const roleChanged = defineEvent({
  type: 'admin.role.changed',
  version: 1,
  payload: z.object({ roleId: Id, change: z.enum(['created', 'updated', 'deleted']) }),
});
export const grantChanged = defineEvent({
  type: 'admin.grant.changed',
  version: 1,
  payload: z.object({
    grantId: Id,
    principalId: Id,
    objectType: ObjectType,
    objectId: Id,
    change: z.enum(['created', 'updated', 'deleted']),
  }),
});
export const tokenChanged = defineEvent({
  type: 'admin.token.changed',
  version: 1,
  payload: z.object({ principalId: Id, tokenId: z.string(), change: z.enum(['issued', 'updated', 'revoked']) }),
});
export const choiceListChanged = defineEvent({
  type: 'admin.choiceList.changed',
  version: 1,
  payload: z.object({ listId: Id, change: z.enum(['created', 'updated', 'deleted', 'values']) }),
});
export const companyProfileChanged = defineEvent({ type: 'admin.company.updated', version: 1, payload: z.object({ tenantId: Id }) });

export const meChanged = defineEvent({
  type: 'admin.me.changed',
  version: 1,
  payload: z.object({ userId: Id }),
});

// ---------- connections (CO-0025, CO-0026) ----------

export const CONNECTION_STATUSES = ['pending', 'active', 'error', 'relink'] as const;

export const ConnectionProvider = z
  .object({
    id: z.string().describe('forgejo or plaud'),
    title: z.string(),
    configured: z.boolean().describe('false while the app has no OAuth client or adapter for this system'),
    link: z.enum(['oauth', 'token']).describe('oauth: link in the browser at startUrl; token: paste the token file with admin.connection.linkToken'),
    startUrl: z.string().nullable().describe('Open this URL in the browser to link your account; it returns to the app'),
    tokenHint: z.string().nullable().describe('How to get the token file, for a token link'),
  })
  .openapi('ConnectionProvider');

export const Connection = z
  .object({
    id: Id,
    userId: Id,
    userName: z.string().nullable(),
    provider: z.string(),
    status: z.enum(CONNECTION_STATUSES).describe('relink: the system refused the token; link the account again'),
    accountLogin: z.string().nullable().describe('Your name in that system'),
    scopes: z.string().nullable(),
    expiresAt: IsoDateTime.nullable().describe('When the access token expires; the app refreshes it before'),
    linkedAt: IsoDateTime.nullable(),
    lastOkAt: IsoDateTime.nullable(),
    lastError: z.string().nullable(),
    lastErrorAt: IsoDateTime.nullable(),
  })
  .openapi('Connection');

export const listConnections = defineAction({
  name: 'admin.connection.list',
  method: 'get',
  path: '/admin/connections',
  permission: AUTHENTICATED,
  input: z.object({
    scope: z.enum(['mine', 'all']).default('mine').describe('all needs admin:read: the links of every user, for support'),
    provider: z.string().max(40).optional(),
  }),
  output: z.object({ providers: z.array(ConnectionProvider), items: z.array(Connection) }),
  summary: 'Your links to external systems (Forgejo) with their status, and the systems you can link; never a token',
});

export const testConnection = defineAction({
  name: 'admin.connection.test',
  method: 'post',
  path: '/admin/connections/{provider}/test',
  permission: AUTHENTICATED,
  successStatus: 200,
  input: z.object({ provider: z.string().max(40) }),
  output: Connection,
  summary: 'Checks your link: refreshes the token when needed and asks the system who you are',
});

export const linkConnectionToken = defineAction({
  name: 'admin.connection.linkToken',
  method: 'post',
  path: '/admin/connections/{provider}/token',
  permission: AUTHENTICATED,
  successStatus: 200,
  input: z.object({
    provider: z.string().max(40).describe('plaud'),
    token: z.string().min(2).max(20_000).describe('The content of the token file of a login on your own laptop, such as ~/.plaud/tokens-mcp.json'),
  }),
  output: Connection,
  restOnly: true,
  secret: true,
  summary: 'Links your account in a system without an OAuth client for the app (Plaud) with the token file of your own login; the app checks it first (ADR-0030)',
  description: 'REST only: a token never goes through a model. Paste the file in the app under Settings, Connections.',
});

export const deleteConnection = defineAction({
  name: 'admin.connection.delete',
  method: 'delete',
  path: '/admin/connections/{provider}',
  permission: AUTHENTICATED,
  input: z.object({
    provider: z.string().max(40),
    userId: Id.optional().describe("Another user's link; needs admin:update"),
  }),
  output: z.object({ deleted: z.literal(true) }),
  summary: 'Removes a link and its tokens from the app; revoke the app in the other system too',
});

export const connectionChanged = defineEvent({
  type: 'admin.connection.changed',
  version: 1,
  payload: z.object({ userId: Id, provider: z.string(), status: z.enum(CONNECTION_STATUSES).nullable() }),
});

export const secretChanged = defineEvent({
  type: 'admin.secret.changed',
  version: 1,
  payload: z.object({ secretId: Id, name: z.string(), organisationId: Id.nullable(), change: z.enum(['created', 'rotated', 'deleted']) }),
});

export const adminEvents = [userChanged, roleChanged, grantChanged, tokenChanged, choiceListChanged, meChanged, companyProfileChanged, secretChanged, connectionChanged];

// ---------- search and saved views (CO-0028, CO-1302) ----------

/** Kinds of record the global search covers; each is the list action of its domain, with that action's permission. */
export const SEARCH_TYPES = ['organisation', 'contact', 'lead', 'meeting', 'proposal', 'project', 'engagement', 'timeEntry', 'invoice', 'issue', 'ticket', 'article'] as const;
export type SearchType = (typeof SEARCH_TYPES)[number];

export const SearchHit = z
  .object({
    type: z.enum(SEARCH_TYPES),
    id: Id,
    title: z.string(),
    subtitle: z.string().nullable().describe('Names that place the record: the client, the project, the repository'),
    status: z.string().nullable().describe('The status code of the record, as its own list action gives it'),
    date: z.string().nullable().describe('The date of a time entry or the issue date of an invoice'),
    path: z.string().describe('Where the web app shows the record'),
  })
  .openapi('SearchHit');

// ---------------------------------------------------------------- personal data on request (CO-0031)

const PrivacyInput = z.object({
  email: z.email().max(320).optional().describe('The address of the person'),
  contactId: Id.optional().describe('A contact (org.contact.list); its address and name come along'),
});

export const privacyExport = defineAction({
  name: 'admin.privacy.export',
  method: 'get',
  path: '/admin/privacy',
  permission: P.keys.export,
  input: PrivacyInput,
  output: z.object({
    subject: z.object({ email: z.string().nullable(), name: z.string().nullable(), contactId: Id.nullable() }),
    generatedAt: IsoDateTime,
    domains: z.array(z.object({ domain: z.string(), items: z.array(z.object({ kind: z.string(), id: z.string(), summary: z.string(), data: z.record(z.string(), z.unknown()) })) })),
  }),
  summary: 'Everything the company holds of a person, per domain, for a request under the GDPR (CO-0031)',
  description: 'For a tenant-wide identity with admin:export. The data is personal: hand it to the person, never to someone else.',
});

export const privacyAnonymise = defineAction({
  name: 'admin.privacy.anonymise',
  method: 'post',
  path: '/admin/privacy/anonymise',
  permission: P.keys.delete,
  input: PrivacyInput.strict(),
  output: z.object({
    results: z.array(
      z.object({
        domain: z.string(),
        changed: z.array(z.object({ kind: z.string(), count: z.number().int() })),
        kept: z.array(z.object({ kind: z.string(), count: z.number().int(), reason: z.string() })),
      }),
    ),
    erasedFiles: z.number().int(),
  }),
  successStatus: 200,
  kind: 'destructive',
  summary: 'Anonymise the personal data of a person in every domain and erase their files; invoices stay under the fiscal duty (CO-0031)',
  description:
    'Cannot be undone. Names become "Geanonimiseerd", addresses go, mails of the person lose their text and files. What the law keeps (invoices, seven years) ' +
    'stays and is listed as kept. An agent may propose it; a person with admin:delete runs it.',
});

export const searchQuery = defineAction({
  name: 'admin.search.query',
  method: 'get',
  path: '/admin/search',
  permission: AUTHENTICATED,
  input: z.object({
    q: z.string().trim().min(2).max(200).describe('Words, a name, a code, an invoice number or #12 for an issue'),
    types: CsvEnum(SEARCH_TYPES).optional().describe('Only these kinds; default all you may read'),
    limit: z.coerce.number().int().min(1).max(20).default(5).describe('Hits per kind'),
  }),
  output: z.object({
    groups: z.array(z.object({ type: z.enum(SEARCH_TYPES), items: z.array(SearchHit) })),
    skipped: z.array(z.enum(SEARCH_TYPES)).describe('Kinds you may not read'),
    failed: z.array(z.enum(SEARCH_TYPES)).describe('Kinds whose search failed; the rest still answers'),
  }),
  summary: 'One search over clients, contacts, projects, engagements, time entries, invoices and issues, with the permissions of each',
});

export const SavedView = z
  .object({
    id: Id,
    listKey: z.string().describe('The list the view belongs to: search for the global search, or a list such as dev.issue.list'),
    name: z.string(),
    params: z.record(z.string(), z.unknown()).describe('The query, filters, columns and sorting of the view'),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .openapi('SavedView');

const ListKey = z.string().trim().min(1).max(60).regex(/^[a-z][a-zA-Z0-9.]*$/);

export const listSavedViews = defineAction({
  name: 'admin.savedView.list',
  method: 'get',
  path: '/admin/saved-views',
  permission: AUTHENTICATED,
  input: z.object({ listKey: ListKey.optional() }),
  output: z.object({ items: z.array(SavedView) }),
  summary: 'Your saved searches and list views, by name',
});

export const saveView = defineAction({
  name: 'admin.savedView.save',
  method: 'put',
  path: '/admin/saved-views',
  permission: AUTHENTICATED,
  successStatus: 200,
  input: z
    .object({
      listKey: ListKey,
      name: z.string().trim().min(1).max(80),
      params: z.record(z.string(), z.unknown()).describe('At most 8000 characters as JSON'),
    })
    .strict(),
  output: SavedView,
  summary: 'Saves a search or list view under a name; the same name replaces it',
});

export const deleteSavedView = defineAction({
  name: 'admin.savedView.delete',
  method: 'delete',
  path: '/admin/saved-views/{id}',
  permission: AUTHENTICATED,
  input: z.object({ id: Id }),
  output: z.object({ deleted: z.literal(true) }),
  summary: 'Removes one of your saved searches or views',
});

// ---------- system connections (CO-0026) ----------

export const SYSTEM_CHECK_STATUSES = ['ok', 'error', 'untested', 'off'] as const;

export const SystemConnection = z
  .object({
    id: z.string().describe('forgejo.mirror, forgejo.feedback, forgejo.oauth, graph.mail, vies, kosit, onepassword'),
    system: z.string().describe('The external system'),
    configured: z.boolean(),
    configDetail: z.string().nullable().describe('What is set, or what is missing; never a secret'),
    testable: z.boolean().describe('Whether the app can test it with a call that changes nothing'),
    status: z.enum(SYSTEM_CHECK_STATUSES).describe('off: not configured; untested: configured but never tested'),
    detail: z.string().nullable().describe('What the last test found'),
    lastCheckedAt: IsoDateTime.nullable(),
    lastOkAt: IsoDateTime.nullable(),
    lastError: z.string().nullable(),
    lastErrorAt: IsoDateTime.nullable(),
  })
  .openapi('SystemConnection');

export const listSystemConnections = defineAction({
  name: 'admin.systemConnection.list',
  method: 'get',
  path: '/admin/system-connections',
  permission: P.keys.read,
  input: z.object({}),
  output: z.object({ items: z.array(SystemConnection) }),
  summary: "The connections of the app itself (Forgejo tokens, Microsoft Graph, VIES, KoSIT, 1Password): configured or not, and the last test",
});

export const testSystemConnection = defineAction({
  name: 'admin.systemConnection.test',
  method: 'post',
  path: '/admin/system-connections/{id}/test',
  permission: P.keys.update,
  successStatus: 200,
  input: z.object({ id: z.string().max(60) }),
  output: SystemConnection,
  summary: 'Tests a connection of the app now with a call that changes nothing, and keeps the result',
});

// ---------- OAuth clients (CO-0018) ----------

export const OAuthClient = z
  .object({
    clientId: z.string(),
    name: z.string().nullable().describe('As the client registered itself, such as Claude Code'),
    principalId: Id,
    principalName: z.string().nullable(),
    connectedAt: IsoDateTime.nullable(),
    scopes: z.array(z.string()),
    readOnly: z.boolean(),
    revokedAt: IsoDateTime.nullable(),
    lastUsedAt: IsoDateTime.nullable(),
  })
  .openapi('OAuthClient');

export const listOAuthClients = defineAction({
  name: 'admin.oauthClient.list',
  method: 'get',
  path: '/admin/oauth-clients',
  permission: AUTHENTICATED,
  input: z.object({ scope: z.enum(['mine', 'all']).default('mine').describe('all needs admin:read') }),
  output: z.object({ items: z.array(OAuthClient) }),
  summary: 'The MCP and OAuth clients connected with your account, or with every account, with their state',
});

export const updateOAuthClient = defineAction({
  name: 'admin.oauthClient.update',
  method: 'patch',
  path: '/admin/oauth-clients/{clientId}',
  permission: AUTHENTICATED,
  successStatus: 200,
  input: z.object({ clientId: z.string().min(1).max(200), principalId: Id.optional().describe("Another person's client; needs admin:update"), readOnly: z.boolean() }).strict(),
  output: OAuthClient,
  summary: 'Sets a connected client to read-only or back (CO-0018)',
});

export const revokeOAuthClient = defineAction({
  name: 'admin.oauthClient.revoke',
  method: 'post',
  path: '/admin/oauth-clients/{clientId}/revoke',
  permission: AUTHENTICATED,
  successStatus: 200,
  input: z.object({ clientId: z.string().min(1).max(200), principalId: Id.optional().describe("Another person's client; needs admin:update") }).strict(),
  output: OAuthClient,
  kind: 'destructive',
  summary: 'Cuts a connected client off at once: its tokens stop working and it must connect again (CO-0018)',
});
